import React, { useRef, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Input,
  Textarea,
  Select,
  Button,
  Field,
  Avatar,
  Combobox,
  Option,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';
import { CreateTaskDialogProps, CARD_COLORS } from './types';

const useStyles = makeStyles({
  dialogSurface: {
    position: 'relative' as any,
    '@media (max-width: 640px)': {
      width: '100vw !important' as any,
      maxWidth: '100vw !important' as any,
      height: '100vh !important' as any,
      maxHeight: '100vh !important' as any,
      borderRadius: '0 !important' as any,
      top: '0 !important' as any,
      left: '0 !important' as any,
      transform: 'none !important' as any,
      display: 'flex !important' as any,
      flexDirection: 'column !important' as any,
    },
  },
  dialogContent: {
    maxHeight: '72vh',
    overflowY: 'auto',
    paddingRight: '12px',
    '@media (max-width: 640px)': {
      maxHeight: 'none',
      flex: '1',
      minHeight: '0',
    },
  },
  formField: {
    marginBottom: tokens.spacingVerticalM,
  },
  colorPicker: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  colorOption: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusSmall,
    border: `2px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorOptionSelected: {
    border: `3px solid ${tokens.colorBrandBackground}`,
  },
  assignmentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  assignmentPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  assignmentPill: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
  },
  dismissButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXXS,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  dateInput: {
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(0.5)',
      cursor: 'pointer',
      opacity: 1,
    },
  },
});

export const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  isOpen,
  onClose,
  onCreateCard,
  newCard,
  setNewCard,
  selectedAssignees,
  validationErrors,
  setValidationErrors,
  availableMembers,
  userPhotos,
  getAvatarColor,
  onAssigneeSelectionChange,
  onRemoveAssignee,
  customUserInput,
  setCustomUserInput,
  onAddCustomUser,
  themedScrollbarClass,
}) => {
  const styles = useStyles();
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open && data.type === 'escapeKeyDown') {
          onClose();
        }
      }}
    >
      <DialogSurface className={styles.dialogSurface} style={{ position: 'relative' }}>
        <Button
          appearance="subtle"
          icon={<Dismiss12Regular />}
          onClick={onClose}
          style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1 }}
        />
        <DialogTitle>Create New Task</DialogTitle>
        <DialogBody>
          <DialogContent
            className={`${themedScrollbarClass} ${styles.dialogContent}`}
          >
            <div className={styles.formField}>
              <Field
                label="Title"
                required
                validationState={validationErrors.title ? 'error' : 'none'}
                validationMessage={validationErrors.title}
              >
                <Input
                  value={newCard.title || ''}
                  onChange={(_, data) => {
                    setNewCard({ ...newCard, title: data.value });
                    if (validationErrors.title && data.value.trim()) {
                      setValidationErrors({ ...validationErrors, title: '' });
                    }
                  }}
                  placeholder="Enter task title"
                  autoComplete="off"
                  ref={titleInputRef}
                />
              </Field>
            </div>

            <div className={styles.formField}>
              <Field label="Description">
                <Textarea
                  value={newCard.description || ''}
                  onChange={(_, data) => setNewCard({ ...newCard, description: data.value })}
                  placeholder="Enter task description"
                  rows={3}
                  autoComplete="off"
                />
              </Field>
            </div>

            <div className={styles.formField}>
              <Field label="Priority">
                <Select
                  value={newCard.priority || 'medium'}
                  onChange={(_, data) => setNewCard({ ...newCard, priority: data.value as any })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </Select>
              </Field>
            </div>

            <div className={styles.formField}>
              <Field label="Task Color">
                <div className={styles.colorPicker}>
                  {CARD_COLORS.map((color) => (
                    <div
                      key={color.value || 'none'}
                      className={`${styles.colorOption} ${newCard.color === color.value ? styles.colorOptionSelected : ''}`}
                      style={{
                        backgroundColor: color.value || tokens.colorNeutralBackground3,
                        border: color.value ? undefined : `2px dashed ${tokens.colorNeutralStroke2}`
                      }}
                      onClick={() => setNewCard({ ...newCard, color: color.value })}
                      title={color.label}
                    >
                      {!color.value && (
                        <span style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>✕</span>
                      )}
                    </div>
                  ))}
                </div>
              </Field>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className={styles.formField} style={{ flex: 1 }}>
                <Field label="Start Date">
                  <Input
                    type="date"
                    value={newCard.startDate || ''}
                    data-has-value={newCard.startDate ? 'true' : 'false'}
                    onChange={(_, data) => {
                      setNewCard({ ...newCard, startDate: data.value });
                      if (validationErrors.endDate) {
                        setValidationErrors({ ...validationErrors, endDate: '' });
                      }
                    }}
                    className={styles.dateInput}
                    autoComplete="off"
                  />
                </Field>
              </div>
              <div className={styles.formField} style={{ flex: 1 }}>
                <Field
                  label="End Date"
                  validationState={validationErrors.endDate ? 'error' : 'none'}
                  validationMessage={validationErrors.endDate}
                >
                  <Input
                    type="date"
                    value={newCard.endDate || ''}
                    data-has-value={newCard.endDate ? 'true' : 'false'}
                    onChange={(_, data) => {
                      setNewCard({ ...newCard, endDate: data.value });
                      if (validationErrors.endDate) {
                        setValidationErrors({ ...validationErrors, endDate: '' });
                      }
                    }}
                    autoComplete="off"
                    className={styles.dateInput}
                  />
                </Field>
              </div>
            </div>

            <div className={styles.formField}>
              <Field label="Assign to">
                <div className={styles.assignmentSection}>
                  {selectedAssignees.length > 0 && (
                    <div className={styles.assignmentPills}>
                      {selectedAssignees.map((userId) => {
                        const member = availableMembers.find(m => m.id === userId);
                        return member ? (
                          <div key={userId} className={styles.assignmentPill}>
                            <Avatar
                              image={userPhotos[userId] ? { src: userPhotos[userId] } : undefined}
                              name={member.displayName}
                              color={getAvatarColor(userId)}
                              size={24}
                            />
                            <Text size={200}>{member.displayName}</Text>
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<Dismiss12Regular />}
                              onClick={() => onRemoveAssignee(userId)}
                              className={styles.dismissButton}
                            />
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  <Combobox
                    multiselect
                    placeholder={selectedAssignees.length > 0 ? "Add more members" : "Select team members"}
                    selectedOptions={selectedAssignees}
                    onOptionSelect={(_, data) => {
                      if (data.selectedOptions) {
                        onAssigneeSelectionChange(data.selectedOptions);
                      }
                    }}
                    autoComplete="off"
                  >
                    {availableMembers.map((member) => (
                      <Option key={member.id} value={member.id} text={member.displayName}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Avatar
                            image={userPhotos[member.id] ? { src: userPhotos[member.id] } : undefined}
                            name={member.displayName}
                            color={getAvatarColor(member.id)}
                            size={24}
                          />
                          <Text>{member.displayName}</Text>
                        </div>
                      </Option>
                    ))}
                  </Combobox>
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                    <Input
                      placeholder="Or type a custom name..."
                      value={customUserInput}
                      onChange={(_, data) => setCustomUserInput(data.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          onAddCustomUser();
                        }
                      }}
                      autoComplete="off"
                      style={{ flex: 1 }}
                    />
                    <Button
                      appearance="secondary"
                      size="small"
                      onClick={onAddCustomUser}
                      disabled={!customUserInput.trim()}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={onCreateCard}
            disabled={!newCard.title?.trim()}
          >
            Create Task
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
